import { gql } from '@apollo/client';

import APClient from '@/lib/graphql/client';
import { routes } from '@/lib/routes';

import type { ReactNode } from 'react';

/**
 * @deprecated The method should not be used, use instead toArray
 */
export const childrenToArray = (
  children: ReactNode | ReactNode[],
): ReactNode[] => (Array.isArray(children) ? children : [children]);

/**
 * Check if the given value is an array and if not convert it to an array
 * @param {any} items The value to convert to an array
 * @returns {any[]} The resulting array
 **/
export const toArray = (items: any): any[] =>
  Array.isArray(items) ? items : [items];

/**
 * TS fails to understand guard for Array.prototype.pop(), so Non-null
 * assertion operator is used to assert that this operand is non-null
 * @see https://github.com/microsoft/TypeScript/issues/30406
 * @see https://www.typescriptlang.org/docs/handbook/release-notes/typescript-2-0.html#non-null-assertion-operator
 **/
// eslint-disable-next-line arrow-body-style
export const getUrlExtension = (url: string): string => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return url?.split(/[#?]/)[0].split('.').pop()!.trim();
};

export const isPNG = (extension: string): boolean =>
  extension?.toLowerCase() === 'png';

export const removeBaseURL = (url: string): string =>
  url.replace(process.env.NEXT_PUBLIC_BACK_URL ?? '', '');

export const isProduction = (): boolean =>
  process.env.NODE_ENV === 'production';

export const debug = (message?: any, ...optionalParams: any[]): void => {
  // eslint-disable-next-line no-console
  if (!isProduction()) console.log(message, ...optionalParams);
};

/**
 * Determine if an object is empty or all values of an object are `null` or `''`
 * @param  {object}   object        Object to check
 * @return {boolean}                Comparison value
 */
export const isEmptyObject = (object: object): boolean =>
  Object.keys(object).length === 0 &&
  object.constructor === Object &&
  Object.values(object).every((x) => x === null || x === '');

/**
 * Transform a constant case string (IN_STOCK) to a lower case (instock)
 * @param  {string}   string        String to convert
 * @return {string}                 String converted
 */
export const constantCaseToLowerCase = (string: string): string =>
  (string || '').replaceAll('_', '').toLocaleLowerCase();

export const isCartRoute = (pathname: string) => pathname === routes.cart;
export const isMyAccountRoute = (pathname: string) =>
  pathname.includes(routes.micuenta.index);

export const formatDateShort = (date: string | Date) =>
  new Date(date).toLocaleString('es-ES', {
    day: 'numeric',
    month: 'short',
    year: '2-digit',
  });

/**
 * Format a date interval in a more human-like language
 * @param {string | Date} startDate
 * @param {string | Date} endDate
 * @returns string with the date formatted
 */
export const formatDateInterval = (
  startDate: string | Date,
  endDate: string | Date,
): string => {
  const months = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ];

  const init = new Date(startDate);
  const end = new Date(endDate);

  return `${init.getDate()} ${
    init.getMonth() !== end.getMonth() ? ` de ${months[init.getMonth()]}` : ``
  } al ${end.getDate()} de ${months[end.getMonth()]}`;
};

export const formartTimeOfSubscription = (
  timeOfSubscription: string,
): string => {
  switch (timeOfSubscription) {
    case '2_week':
      return 'Cada 2 semanas';
    case '4_week':
      return 'Cada 4 semanas';
    case '6_week':
      return 'Cada 6 semanas';
    case '8_week':
      return 'Cada 8 semanas';
    default:
      return 'Cada 4 semanas';
  }
};

export const getAppliedFiltersToURL = async (params: string[]) => {
  const realUrl: string[] = [];
  let filterString = '';
  let realCategory = '';
  let filterSlug = '';
  let filterValue = '';

  const { data } = await APClient.query({
    query: gql`
      query FiltersQueryUrl {
        filtrableFields {
          seo
        }
      }
    `,
  });

  params.forEach((item: any) => {
    const found = data.filtrableFields.find((field: any) => {
      const testString = `${field.seo}-`;
      return item.startsWith(testString);
    });

    if (!found) {
      realCategory = item;
      realUrl.push(item);
    } else {
      filterSlug = found.seo;
      filterValue = item.replace(`${found.seo}-`, '');
      filterString = `${filterString} AND ${found.seo}:"${item.replace(
        `${found.seo}-`,
        '',
      )}"`;
    }
  });

  return {
    arrayOfCategories: realUrl,
    algoliaFilter: filterString,
    wordpressSlug: realCategory,
    filterSlug,
    filterValue,
    url: `/${params.join('/')}`,
  };
};

export const getPagePropsForAppliedFiltersToURL = (
  pageProps: any,
  url: string,
) => {
  const { customSeo, ...wantedProps } = pageProps;
  const newPageProps = structuredClone(wantedProps);

  if (customSeo?.customFields?.miraklfilterSeo?.titleSeo) {
    newPageProps.seo.title = customSeo.customFields.miraklfilterSeo.titleSeo;
  } else if (customSeo?.titleSeo) {
    newPageProps.seo.title = customSeo.titleSeo;
  }

  if (customSeo?.customFields?.miraklfilterSeo?.descriptionSeo) {
    newPageProps.seo.metaDesc =
      customSeo.customFields.miraklfilterSeo.descriptionSeo;
  } else if (customSeo?.descriptionSeo) {
    newPageProps.seo.metaDesc = customSeo.descriptionSeo;
  }

  newPageProps.seo.canonical = `${process.env.NEXT_PUBLIC_FRONT_URL?.replace(
    /\/$/,
    '',
  )}${url}`;

  if (customSeo?.customFields?.miraklfilterSeo?.flexibleContent) {
    const updateTypeFlexible = (value: string) =>
      value.replace(
        'FilterSEO_Miraklfilterseo',
        'ProductCategory_Contenidoflexible',
      );

    newPageProps.flexibleContent.flexible =
      customSeo?.customFields?.miraklfilterSeo?.flexibleContent
        .filter((flexible: any) => flexible.fieldGroupName !== undefined)
        .map((flexible: any) => ({
          ...flexible,
          fieldGroupName: updateTypeFlexible(flexible.fieldGroupName),
          __typename: updateTypeFlexible(flexible.__typename),
        }));
  }

  return newPageProps;
};

export const kebabCase = (str: string) =>
  str
    ?.normalize('NFD')
    ?.replace(/[\u0300-\u036f]/g, '')
    ?.replace(/([a-z])([A-Z])/g, '$1-$2')
    ?.replace(/\s+/g, '-')
    ?.toLowerCase();

export const getBreadcrumbsByUrl = (path: string) => {
  const breadcrumbs: {
    name: string;
    url: string;
  }[] = [
    {
      name: 'bigcrafters',
      url: '/',
    },
  ];

  let urlBuilder = '/';

  const urlRegex = /\//g;
  const nameRegex = /\?|\#/g;

  const urlSplitted = path.split(urlRegex).slice(1);

  urlSplitted.forEach((url) => {
    urlBuilder = `${urlBuilder}${url}/`;

    if (url !== 'marcas' && url !== 'vendedores') {
      breadcrumbs.push({
        name: url.split(nameRegex)[0].replaceAll('-', ' '),
        url: urlBuilder,
      });
    }
  });

  return breadcrumbs;
};
